import { useMemo } from "react";
import { firstName } from "@/lib/format";

/**
 * Picks the dashboard greeting line for the current session. Deterministic
 * per (date, user_id, name) tuple so it doesn't flicker on every re-render
 * but DOES rotate each new day. Picks from buckets based on:
 *   1. Streak milestone overrides (>= 3, 7, 30) -- single, earned line.
 *   2. Returning-after-gap (last seen 3+ days) -- "we saved your spot" tone.
 *   3. Time of day -- morning / afternoon / evening / night.
 *   4. New-user welcome -- no progress yet.
 *
 * Names are inserted ~70% of the time so the greeting doesn't feel scripted.
 *
 * The seed mixes the calendar date AND the user id so two students on the
 * same day see different lines.
 */

export type GreetingInput = {
  name: string | null;
  userId: string | null;
  /** ISO timestamp of the user's most recent activity, if any. */
  lastSeenIso?: string | null;
  /** Current streak in days (0 if not on a streak). */
  streakDays?: number;
  /** True if the user has zero recorded activity. */
  isBrandNew?: boolean;
};

/**
 * Module-scoped seed -- computed once when this module first loads on
 * the page. Every hard refresh / new tab gets a fresh seed, so the
 * greeting line (and name-personalisation coin-flip) rotates on
 * reload. Re-renders, route changes, and React strict-mode double-
 * invocations within the same SPA session reuse the same seed so the
 * line doesn't flicker mid-visit.
 */
const SESSION_SEED = Math.floor(Math.random() * 0xffffffff);

export function useGreeting(input: GreetingInput): string {
  const { name, userId, lastSeenIso, streakDays = 0, isBrandNew = false } = input;
  return useMemo(() => {
    const fn = firstName(name);
    // Name shown ~70% of the time -- randomised per page load, not
    // sticky across days. Coin-flips a different way on each refresh.
    const useName = (SESSION_SEED >>> 4) % 10 < 7;
    const hour = new Date().getHours();
    const seed = SESSION_SEED;

    // 1. Streak milestone overrides -- these feel earned.
    if (streakDays >= 30) return pick(STREAK_30, seed);
    if (streakDays >= 7)  return pick(STREAK_7,  seed);
    if (streakDays >= 3)  return pick(STREAK_3,  seed);

    // 2. Returning after a gap (last activity 3+ days ago).
    if (lastSeenIso) {
      const daysSince = (Date.now() - new Date(lastSeenIso).getTime()) / (1000 * 60 * 60 * 24);
      if (daysSince >= 3) return pick(RETURNING, seed);
    }

    // 3. Brand new -- never logged anything.
    if (isBrandNew) return pick(NEW_USER, seed);

    // 4. Time of day.
    const pool = poolForHour(hour);
    const tpl = pool[seed % pool.length];
    return interpolate(tpl, fn, useName);
    // userId stays in the dep list so a sign-in / sign-out during the
    // session refreshes the line; the seed itself is module-scoped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, userId, lastSeenIso, streakDays, isBrandNew]);
}

// ---------------------------------------------------------------------------
// Greeting pools. `{name}` placeholder gets filled in iff useName fires.
// Lines without a placeholder are name-free by design.
// ---------------------------------------------------------------------------

const MORNING = [
  "Morning, {name}.",
  "Good morning. Coffee or Physics first?",
  "Early start. Respect.",
];

const AFTERNOON = [
  "Afternoon. Quick win?",
  "Welcome back, {name}.",
  "Pick up where you stopped?",
];

const EVENING = [
  "Wrapping up the day with a chapter?",
  "Nice to see you, {name}.",
];

const NIGHT = [
  "Late one, {name}. We won't tell.",
  "Insomnia or inspiration, {name}?",
  "Night owl mode, {name}.",
  "Catching up at night, {name}. Real ones do.",
  "Late-night focus, the rare kind.",
  "Pre-sleep revision, {name}?",
];

const STREAK_3 = [
  "3 days strong. Don't break the chain.",
];

const STREAK_7 = [
  "A week of focus. This is how it sticks.",
];

const STREAK_30 = [
  "30 days. You're the example now.",
];

const RETURNING = [
  "Welcome back. We saved your spot.",
];

const NEW_USER = [
  "Welcome to StaxED.",
];

function poolForHour(hour: number): string[] {
  if (hour >= 5 && hour < 11) return MORNING;
  if (hour >= 11 && hour < 17) return AFTERNOON;
  if (hour >= 17 && hour < 22) return EVENING;
  return NIGHT;
}

/**
 * Drop {name} placeholders to "you"-form when not personalising, otherwise
 * substitute. Lines that don't have a placeholder pass through unchanged.
 */
function interpolate(template: string, name: string, useName: boolean): string {
  if (!template.includes("{name}")) return template;
  if (useName && name !== "you") {
    return template.replace("{name}", name);
  }
  // Drop the placeholder + the comma+space before it ("Morning, {name}." -> "Morning.")
  return template
    .replace(/,\s*\{name\}/g, "")
    .replace(/\s*\{name\}/g, "");
}

function pick<T>(arr: T[], seed: number): T {
  return arr[seed % arr.length];
}
